import { loadScript, removeScript } from './dom';
import { logError } from './logging';
import type {
  AfterShipAdsClient,
  AfterShipAdsEnvironment,
  AfterShipAdsLoggerLike,
  AfterShipAdsShowAttributes,
} from './types';

export interface AfterShipAdsOptions {
  appKey: string;
  environment?: AfterShipAdsEnvironment;
  logger?: AfterShipAdsLoggerLike;
}

/**
 * Thin framework-agnostic wrapper over the AfterShip Ads Web SDK.
 *
 * Every call is guarded even though the SDK is documented as fail-soft: a third-party ad
 * must never be able to break the page it sits on.
 */
export class AfterShipAds {
  private appKey: string;
  private environment?: AfterShipAdsEnvironment;
  private logger: AfterShipAdsLoggerLike;
  private client: AfterShipAdsClient | null = null;

  constructor({ appKey, environment, logger }: AfterShipAdsOptions) {
    this.appKey = appKey;
    this.environment = environment;
    this.logger = logger ?? console;
  }

  /**
   * Loads the SDK and creates a client bound to `mount`, which must already be in the DOM.
   * Returns `false` when that failed, in which case the caller renders nothing.
   */
  async create(mount: HTMLElement): Promise<boolean> {
    if (!this.appKey) {
      logError(this.logger, 'create', 'No appKey provided');
      return false;
    }

    const astag = await loadScript(this.logger);
    if (!astag) return false;

    try {
      this.client = astag.create({
        appKey: this.appKey,
        mount,
        ...(this.environment ? { environment: this.environment } : {}),
      });
      return true;
    } catch (err) {
      logError(this.logger, 'create', 'Failed to create ads client', err);
      this.client = null;
      return false;
    }
  }

  /** Requests and renders one ad. Resolves `false` when nothing was rendered. */
  async show(attributes: AfterShipAdsShowAttributes): Promise<boolean> {
    if (!this.client) return false;

    try {
      await this.client.show(attributes);
      return true;
    } catch (err) {
      logError(this.logger, 'show', 'Failed to show ad', err);
      return false;
    }
  }

  /**
   * Tears the rendered ad down. Must run on route change / unmount so no stale ad frame is
   * left behind — both host pages are client-side routed.
   */
  close(): void {
    if (!this.client) return;

    try {
      this.client.close();
    } catch (err) {
      logError(this.logger, 'close', 'Failed to close ad', err);
    } finally {
      this.client = null;
    }
  }

  /** Closes any open ad and removes the SDK tag. Used when consent is revoked mid-session. */
  teardown(): void {
    this.close();
    removeScript(this.logger);
  }

  isCreated(): boolean {
    return this.client !== null;
  }
}
